/**
 * Custom hook for managing Performance Settings state and API operations
 */
import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { useQualityMeasureLabel } from '../../../lib/qualityLabels';
import {
  performanceSettingsAPI,
  SupplierPerformanceSettings,
  SupplierRatingThreshold,
  CreateRatingThresholdData,
} from '../../../lib/api';
import { getApiErrorMessage } from '../../../lib/api/types/common';
import { useConfirm } from '../../../contexts/ConfirmDialogContext';
import {
  PerformanceFormData,
  ThresholdEditData,
  SupplierEditData,
} from './types';

export function usePerformanceSettings() {
  const confirm = useConfirm();
  const qualityLabel = useQualityMeasureLabel();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<SupplierPerformanceSettings | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Form state
  const [formData, setFormData] = useState<PerformanceFormData>({
    // KPI Target Values (for dashboard graphs)
    otif_target: '95.00',
    otd_target: '95.00',
    ppm_target: 500,
    // Color Thresholds
    ppm_excellent_threshold: 500,
    ppm_good_threshold: 2000,
    ppm_poor_threshold: 5000,
    otif_weight: '70.00',
    ppm_weight: '30.00',
    otif_days_early_allowed: 21,
    otif_days_late_allowed: 1,
    include_arrears_in_otif: true,
    arrears_buffer_days: 0,
    default_firm_days: 14,
    delivery_metric_mode: 'both',
  });

  // Threshold editing state
  const [editingThreshold, setEditingThreshold] = useState<number | null>(null);
  const [thresholdEditData, setThresholdEditData] = useState<ThresholdEditData | null>(null);
  const [savingThreshold, setSavingThreshold] = useState(false);

  // Add threshold state
  const [showAddThreshold, setShowAddThreshold] = useState(false);
  const [newThresholdData, setNewThresholdData] = useState<CreateRatingThresholdData>({
    grade: '',
    label: '',
    min_score: '0',
    max_score: '100',
    color: '#3B82F6',
    description: '',
    order: 0,
  });
  const [addingThreshold, setAddingThreshold] = useState(false);

  // Supplier OTIF Override state
  const [showSupplierOverrides, setShowSupplierOverrides] = useState(false);
  const [supplierSearch, setSupplierSearch] = useState('');
  const [editingSupplier, setEditingSupplier] = useState<number | null>(null);
  const [supplierEditData, setSupplierEditData] = useState<SupplierEditData | null>(null);
  const [savingSupplier, setSavingSupplier] = useState(false);

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      const response = await performanceSettingsAPI.getSettings();
      setSettings(response.data);
      setFormData({
        // KPI Target Values (for dashboard graphs)
        otif_target: response.data.otif_target,
        otd_target: response.data.otd_target,
        ppm_target: response.data.ppm_target,
        // Color Thresholds
        ppm_excellent_threshold: response.data.ppm_excellent_threshold,
        ppm_good_threshold: response.data.ppm_good_threshold,
        ppm_poor_threshold: response.data.ppm_poor_threshold,
        otif_weight: response.data.otif_weight,
        ppm_weight: response.data.ppm_weight,
        otif_days_early_allowed: response.data.otif_days_early_allowed,
        otif_days_late_allowed: response.data.otif_days_late_allowed,
        include_arrears_in_otif: response.data.include_arrears_in_otif,
        arrears_buffer_days: response.data.arrears_buffer_days ?? 0,
        default_firm_days: response.data.default_firm_days ?? 14,
        delivery_metric_mode: response.data.delivery_metric_mode || 'both',
      });
      setHasChanges(false);
    } catch (error) {
      console.error('Failed to load performance settings:', error);
      toast.error('Failed to load performance settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleInputChange = useCallback((field: string, value: string | number | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  }, []);

  const handleWeightChange = useCallback((field: 'otif_weight' | 'ppm_weight', value: string) => {
    const numValue = parseFloat(value) || 0;
    const otherField = field === 'otif_weight' ? 'ppm_weight' : 'otif_weight';
    const otherValue = (100 - numValue).toFixed(2);

    setFormData(prev => ({
      ...prev,
      [field]: value,
      [otherField]: otherValue,
    }));
    setHasChanges(true);
  }, []);

  const handleSave = useCallback(async () => {
    try {
      setSaving(true);
      const response = await performanceSettingsAPI.updateSettings(formData);
      // The PATCH returns the full settings object directly; the previous
      // `response.data.data` read was undefined at runtime, blanking the
      // tab's state after every save.
      setSettings(response.data);
      setHasChanges(false);
      toast.success('Performance settings saved successfully');
    } catch (error: unknown) {
      console.error('Failed to save performance settings:', error);
      toast.error(getApiErrorMessage(error, 'Failed to save settings'));
    } finally {
      setSaving(false);
    }
  }, [formData]);

  // Threshold operations
  const handleResetThresholds = useCallback(async () => {
    if (!settings) return;
    const ok = await confirm('Reset all rating thresholds to default values?');
    if (!ok) return;

    try {
      await performanceSettingsAPI.resetRatingThresholds(settings.id);
      await loadSettings();
      toast.success('Rating thresholds reset to defaults');
    } catch (error) {
      console.error('Failed to reset thresholds:', error);
      toast.error('Failed to reset thresholds');
    }
  }, [settings, loadSettings, confirm]);

  const handleResetOTIFMappings = useCallback(async () => {
    if (!settings) return;
    const ok = await confirm('Reset all OTIF score mappings to default values?');
    if (!ok) return;

    try {
      await performanceSettingsAPI.resetOTIFScoreMappings(settings.id);
      await loadSettings();
      toast.success('OTIF score mappings reset to defaults');
    } catch (error) {
      console.error('Failed to reset OTIF mappings:', error);
      toast.error('Failed to reset OTIF mappings');
    }
  }, [settings, loadSettings, confirm]);

  const handleResetPPMMappings = useCallback(async () => {
    if (!settings) return;
    const ok = await confirm(`Reset all ${qualityLabel} score mappings to default values?`);
    if (!ok) return;

    try {
      await performanceSettingsAPI.resetPPMScoreMappings(settings.id);
      await loadSettings();
      toast.success(`${qualityLabel} score mappings reset to defaults`);
    } catch (error) {
      console.error(`Failed to reset ${qualityLabel} mappings:`, error);
      toast.error(`Failed to reset ${qualityLabel} mappings`);
    }
  }, [settings, loadSettings, confirm]);

  const handleEditThreshold = useCallback((threshold: SupplierRatingThreshold) => {
    setEditingThreshold(threshold.id);
    setThresholdEditData({
      grade: threshold.grade,
      label: threshold.label || '',
      min_score: threshold.min_score,
      max_score: threshold.max_score,
      color: threshold.color,
      description: threshold.description,
      order: threshold.order,
    });
  }, []);

  const handleCancelEditThreshold = useCallback(() => {
    setEditingThreshold(null);
    setThresholdEditData(null);
  }, []);

  const handleSaveThreshold = useCallback(async (thresholdId: number) => {
    if (!thresholdEditData) return;

    try {
      setSavingThreshold(true);
      await performanceSettingsAPI.updateRatingThreshold(thresholdId, {
        grade: thresholdEditData.grade,
        label: thresholdEditData.label,
        min_score: thresholdEditData.min_score,
        max_score: thresholdEditData.max_score,
        color: thresholdEditData.color,
        description: thresholdEditData.description,
        order: thresholdEditData.order,
      });
      await loadSettings();
      setEditingThreshold(null);
      setThresholdEditData(null);
      toast.success('Grade threshold updated successfully');
    } catch (error: unknown) {
      console.error('Failed to update threshold:', error);
      toast.error(getApiErrorMessage(error, 'Failed to update threshold'));
    } finally {
      setSavingThreshold(false);
    }
  }, [thresholdEditData, loadSettings]);

  const handleThresholdFieldChange = useCallback((field: string, value: string | number) => {
    setThresholdEditData(prev => prev ? { ...prev, [field]: value } : null);
  }, []);

  const handleAddThreshold = useCallback(async () => {
    if (!newThresholdData.grade.trim()) {
      toast.error('Please enter a grade identifier');
      return;
    }

    try {
      setAddingThreshold(true);
      const maxOrder = settings?.rating_thresholds?.reduce((max, t) => Math.max(max, t.order), 0) || 0;
      await performanceSettingsAPI.createRatingThreshold({
        ...newThresholdData,
        order: maxOrder + 1,
      });
      await loadSettings();
      setShowAddThreshold(false);
      setNewThresholdData({
        grade: '',
        label: '',
        min_score: '0',
        max_score: '100',
        color: '#3B82F6',
        description: '',
        order: 0,
      });
      toast.success('New grade threshold added successfully');
    } catch (error: unknown) {
      console.error('Failed to add threshold:', error);
      toast.error(getApiErrorMessage(error, 'Failed to add threshold'));
    } finally {
      setAddingThreshold(false);
    }
  }, [newThresholdData, settings, loadSettings]);

  const handleDeleteThreshold = useCallback(async (thresholdId: number, label: string) => {
    const ok = await confirm({ title: 'Delete Threshold', description: `Are you sure you want to delete the "${label}" grade threshold?`, confirmLabel: 'Delete', variant: 'destructive' });
    if (!ok) {
      return;
    }

    try {
      await performanceSettingsAPI.deleteRatingThreshold(thresholdId);
      await loadSettings();
      toast.success('Grade threshold deleted successfully');
    } catch (error: unknown) {
      console.error('Failed to delete threshold:', error);
      toast.error(getApiErrorMessage(error, 'Failed to delete threshold'));
    }
  }, [loadSettings, confirm]);

  const handleNewThresholdFieldChange = useCallback((field: string, value: string | number) => {
    setNewThresholdData(prev => ({ ...prev, [field]: value }));
  }, []);

  // Supplier OTIF Override handlers
  const handleEditSupplier = useCallback((supplier: {
    id: number;
    use_custom_otif_window: boolean;
    otif_days_early_allowed: number;
    otif_days_late_allowed: number;
  }) => {
    setEditingSupplier(supplier.id);
    setSupplierEditData({
      use_custom_otif_window: supplier.use_custom_otif_window,
      otif_days_early_allowed: supplier.otif_days_early_allowed,
      otif_days_late_allowed: supplier.otif_days_late_allowed,
    });
  }, []);

  const handleCancelEditSupplier = useCallback(() => {
    setEditingSupplier(null);
    setSupplierEditData(null);
  }, []);

  const handleSaveSupplier = useCallback(async (supplierId: number) => {
    if (!supplierEditData) return;

    try {
      setSavingSupplier(true);
      await performanceSettingsAPI.updateSupplierOTIFOverride(supplierId, supplierEditData);
      await loadSettings();
      setEditingSupplier(null);
      setSupplierEditData(null);
      toast.success('Supplier OTIF settings updated successfully');
    } catch (error: unknown) {
      console.error('Failed to update supplier OTIF:', error);
      toast.error(getApiErrorMessage(error, 'Failed to update supplier OTIF settings'));
    } finally {
      setSavingSupplier(false);
    }
  }, [supplierEditData, loadSettings]);

  const handleToggleSupplierCustom = useCallback(async (supplier: {
    id: number;
    use_custom_otif_window: boolean;
  }) => {
    try {
      setSavingSupplier(true);
      await performanceSettingsAPI.updateSupplierOTIFOverride(supplier.id, {
        use_custom_otif_window: !supplier.use_custom_otif_window,
      });
      await loadSettings();
      toast.success(
        supplier.use_custom_otif_window
          ? 'Supplier now using default OTIF window'
          : 'Supplier now using custom OTIF window'
      );
    } catch (error: unknown) {
      console.error('Failed to toggle supplier OTIF:', error);
      toast.error(getApiErrorMessage(error, 'Failed to toggle OTIF settings'));
    } finally {
      setSavingSupplier(false);
    }
  }, [loadSettings]);

  return {
    // State
    loading,
    saving,
    settings,
    hasChanges,
    formData,
    // Threshold state
    editingThreshold,
    thresholdEditData,
    savingThreshold,
    showAddThreshold,
    newThresholdData,
    addingThreshold,
    // Supplier state
    showSupplierOverrides,
    supplierSearch,
    editingSupplier,
    supplierEditData,
    savingSupplier,
    // State setters
    setShowAddThreshold,
    setShowSupplierOverrides,
    setSupplierSearch,
    setSupplierEditData,
    // Handlers
    handleInputChange,
    handleWeightChange,
    handleSave,
    handleResetThresholds,
    handleResetOTIFMappings,
    handleResetPPMMappings,
    handleEditThreshold,
    handleCancelEditThreshold,
    handleSaveThreshold,
    handleThresholdFieldChange,
    handleAddThreshold,
    handleDeleteThreshold,
    handleNewThresholdFieldChange,
    handleEditSupplier,
    handleCancelEditSupplier,
    handleSaveSupplier,
    handleToggleSupplierCustom,
    loadSettings,
  };
}
